import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Copy, ShieldCheck, Save } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/stores/toast';
import { useAuthStore } from '@/stores/auth';

interface SsoConfig {
  id?: string;
  tenantId: string;
  protocol: 'SAML2';
  isEnabled: boolean;
  idpEntityId?: string | null;
  idpSsoUrl?: string | null;
  idpCertificate?: string | null;
  spEntityId?: string | null;
  defaultRole: 'VIEWER' | 'OPERATOR' | 'REVIEWER' | 'MANAGER';
  attributeEmail: string;
  attributeName: string;
}

export function SettingsSsoPage() {
  const qc = useQueryClient();
  const tenant = useAuthStore((s) => s.tenant);
  const [draft, setDraft] = useState<SsoConfig | null>(null);

  const config = useQuery({
    queryKey: ['admin', 'sso'],
    queryFn: async () => (await api.get<SsoConfig>('/api/admin/sso')).data,
  });

  useEffect(() => {
    if (config.data && !draft) setDraft(config.data);
  }, [config.data, draft]);

  const save = useMutation({
    mutationFn: async () => {
      if (!draft) return;
      return api.put('/api/admin/sso', {
        protocol: draft.protocol,
        isEnabled: draft.isEnabled,
        idpEntityId: draft.idpEntityId || undefined,
        idpSsoUrl: draft.idpSsoUrl || undefined,
        idpCertificate: draft.idpCertificate || undefined,
        spEntityId: draft.spEntityId || undefined,
        defaultRole: draft.defaultRole,
        attributeEmail: draft.attributeEmail,
        attributeName: draft.attributeName,
      });
    },
    onSuccess: () => {
      toast.success('SSO config saved', `SAML ${draft?.isEnabled ? 'enabled' : 'disabled'}.`);
      qc.invalidateQueries({ queryKey: ['admin', 'sso'] });
    },
    onError: (err) => toast.error('Save failed', extractApiError(err).message),
  });

  function copy(text: string) {
    void navigator.clipboard.writeText(text);
    toast.success('Copied');
  }

  if (config.isLoading || !draft) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const apiUrl = (typeof window !== 'undefined' ? window.location.origin.replace(':5173', ':3000').replace(':5174', ':3000') : '');
  const callbackUrl = `${apiUrl}/sso/saml/callback/${tenant?.slug}`;
  const loginUrl = `${apiUrl}/sso/saml/login/${tenant?.slug}`;
  const metadataUrl = `${apiUrl}/api/admin/sso/sp-metadata`;
  const defaultSpEntity = draft.spEntityId || `${apiUrl}/sso/saml/sp/${tenant?.slug}`;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-2xl">SAML SSO</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Per-tenant SAML 2.0 single sign-on. Send your IdP admin the metadata + ACS URL below; paste the IdP details on the right; flip the toggle.
          </p>
        </div>
        <Badge variant={draft.isEnabled ? 'success' : 'secondary'}>
          {draft.isEnabled ? 'Enabled' : 'Disabled'}
        </Badge>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" /> Hand these to your IdP admin
          </CardTitle>
          <CardDescription>Praxara is the Service Provider. Your IdP issues the assertion.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <CopyRow label="SP Entity ID" value={defaultSpEntity} onCopy={copy} />
          <CopyRow label="ACS URL (where the IdP POSTs SAMLResponse)" value={callbackUrl} onCopy={copy} />
          <CopyRow label="Tenant login URL (give your users)" value={loginUrl} onCopy={copy} />
          <CopyRow label="SP metadata XML (admin-only download)" value={metadataUrl} onCopy={copy} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-primary" /> IdP details
          </CardTitle>
          <CardDescription>From your IdP's metadata. Required to enable SSO.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Field label="IdP Entity ID (issuer)" placeholder="https://login.yourcompany.com/saml/idp" value={draft.idpEntityId ?? ''} onChange={(v) => setDraft({ ...draft, idpEntityId: v })} />
          <Field label="IdP SSO URL (HTTP-Redirect / HTTP-POST)" placeholder="https://login.yourcompany.com/saml/sso" value={draft.idpSsoUrl ?? ''} onChange={(v) => setDraft({ ...draft, idpSsoUrl: v })} />
          <div>
            <Label htmlFor="cert">IdP X.509 certificate (PEM)</Label>
            <Textarea
              id="cert"
              value={draft.idpCertificate ?? ''}
              onChange={(e) => setDraft({ ...draft, idpCertificate: e.target.value })}
              placeholder="-----BEGIN CERTIFICATE-----&#10;MIIDazCCAlOg...&#10;-----END CERTIFICATE-----"
              rows={8}
              className="font-mono text-xs"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Attribute mapping</CardTitle>
          <CardDescription>Which SAML attribute names carry the user's email + display name.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <Field label="Email attribute" placeholder="email" value={draft.attributeEmail} onChange={(v) => setDraft({ ...draft, attributeEmail: v })} />
          <Field label="Display-name attribute" placeholder="displayName" value={draft.attributeName} onChange={(v) => setDraft({ ...draft, attributeName: v })} />
          <div className="md:col-span-2">
            <Label htmlFor="role">Default role for JIT-provisioned users</Label>
            <select
              id="role"
              value={draft.defaultRole}
              onChange={(e) => setDraft({ ...draft, defaultRole: e.target.value as SsoConfig['defaultRole'] })}
              className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
            >
              <option value="VIEWER">Viewer (read-only)</option>
              <option value="OPERATOR">Operator</option>
              <option value="REVIEWER">Reviewer</option>
              <option value="MANAGER">Manager</option>
            </select>
            <p className="mt-1 text-xs text-muted-foreground">First-time SAML logins create a user with this role. Promote individual users in <strong>Settings → Team</strong> after.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Enable SSO</CardTitle>
          <CardDescription>Once enabled, users at this tenant can sign in via the IdP. Local password login still works (you can disable it from <strong>Settings → Security</strong>).</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.isEnabled}
              onChange={(e) => setDraft({ ...draft, isEnabled: e.target.checked })}
            />
            Enabled
          </label>
          <Button onClick={() => save.mutate()} disabled={save.isPending} className="ml-auto">
            <Save className="mr-1.5 h-4 w-4" /> {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, placeholder, value, onChange }: { label: string; placeholder?: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function CopyRow({ label, value, onCopy }: { label: string; value: string; onCopy: (s: string) => void }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 flex items-center gap-2">
        <code className="flex-1 truncate rounded-md border bg-muted/30 px-3 py-2 font-mono text-[11px]">{value}</code>
        <Button variant="ghost" size="sm" onClick={() => onCopy(value)}>
          <Copy className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
